import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter
} from '@cat-ecom/pcc-components';
import { useTranslation } from 'react-i18next';
import { CatButton } from '@blocks-react/components/Button';
import { Validator } from 'cat-ecommerce-alloy';
import FormField from '@app/components/common/Blocks/AddressFormFields/FormField';
import PhoneNumberInput from '@app/components/common/Blocks/AddressFormFields/PhoneNumberInput';
import { AFFILIATION_DEALER } from '../../../../constants/commonConstants';
import { replaceTokensInString } from '@app/utils';
import { capitalizeText } from '@app/utils/stringUtils';
import cx from 'classnames';
import styles from './SummaryOrderByMobileDrawer.module.scss';
import CartSaveToListButton from '../../checkout/shopping-cart/OrderItems/common/CartSaveToListButton';

const SummaryOrderByMobileDrawer = ({
  countryDisplayName,
  handleUpdate,
  ...orderByMobileDrawerProps
}) => {
  const [t] = useTranslation();

  const isPCC_EditPhoneNumberFormat = useSelector(
    s => s.featureFlag?.PCC_EditPhoneNumberFormat
  );
  const FIELD_REQUIRED_MSG = t('1626_FIELD_REQUIRED_ERROR');

  const userAffiliation = useSelector(s => s.common.userAffiliation);
  const isDealerUser = userAffiliation === AFFILIATION_DEALER;

  const getEmailValidator = useCallback(
    currentValue => {
      return new Validator(currentValue)
        .required(
          replaceTokensInString(FIELD_REQUIRED_MSG, t('CAT_EMAIL_EMAIL1'))
        )
        .isValidEmail(t('ERROR_INVALIDEMAILFORMAT'))
        .getError();
    },
    [FIELD_REQUIRED_MSG, t]
  );

  const getPhoneValidator = useCallback(
    currentValue => {
      return new Validator(currentValue)
        .required(
          replaceTokensInString(FIELD_REQUIRED_MSG, t('CAT_EMAIL_PHONE1'))
        )
        .isValidPhoneNumber(t('ERROR_INVALIDPHONE'))
        .getError();
    },
    [FIELD_REQUIRED_MSG, t]
  );

  return (
    <Drawer {...orderByMobileDrawerProps.drawerProps}>
      <DrawerHeader>
        <h4
          className={cx(
            'cat-u-theme-typography-title m-0 text-capitalize',
            styles['order-by-mobile__title']
          )}
        >
          {t('EDIT_ORDERED_BY_TOGGLE')}
        </h4>
      </DrawerHeader>
      <DrawerBody>
        <div className="d-flex flex-column gap-3">
          <FormField
            fieldId="name"
            required
            label={capitalizeText(t('ORDERED_BY_LABEL'))}
            maxLength="40"
            enabled={!isDealerUser}
          />

          <FormField
            fieldId="email"
            required
            label={t('CAT_EMAIL_EMAIL1')}
            customValidation={getEmailValidator}
            maxLength="50"
          />

          {isPCC_EditPhoneNumberFormat ? (
            <PhoneNumberInput
              fieldId="phone"
              showRequired
              selectedCountry={countryDisplayName}
            />
          ) : (
            <FormField
              fieldId="phone"
              required
              label={t('CAT_EMAIL_PHONE1')}
              customValidation={getPhoneValidator}
              maxLength="16"
            />
          )}
        </div>
      </DrawerBody>
      <DrawerFooter className="d-flex gap-3">
        {handleUpdate ? (
          <CatButton onClick={handleUpdate} variant="primary" fullwidth>
            {t('UPDATE')}
          </CatButton>
        ) : (
          <CatButton type="submit" variant="primary" fullwidth>
            {t('UPDATE')}
          </CatButton>
        )}
        <CatButton
          variant="ghost"
          fullwidth
          onClick={orderByMobileDrawerProps.closeDrawer}
        >
          {t('CANCEL')}
        </CatButton>
      </DrawerFooter>
    </Drawer>
  );
};

SummaryOrderByMobileDrawer.propTypes = {
  countryDisplayName: PropTypes.string,
  handleUpdate: PropTypes.func
};

export default SummaryOrderByMobileDrawer;
